import {
  Table,
  Flex,
  TableContainer,
  Text,
  Divider,
  spaces,
  sizes,
} from '@electricjs/arc';
import { useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import PerformedByCell from '@/components/Assets/AssetEventsHistory/PerformedByCell';
import formatDate from '@/helpers/formatDate';
import { EventCell } from '@/components/Assets/AssetEventsHistory/EventCell';
import { AssetEvent } from '@/types/assets';
import {
  CategoryMap,
  EventTypeMap,
} from '@/components/Assets/AssetEventsHistory/types';
import { InfiniteScroll } from '@common';
import TableFiltersGroup from '@/components/Assets/AssetEventsHistory/TableFiltersGroups';
import { useSearchParams } from 'react-router-dom';
import { AssetEventsHistoryQueryParams } from '@/types/queryParamKeys';
import { SortOrder } from '@electricjs/core_entity-client';
import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import { DownloadAllEventsHistoryButton } from '@/components/Assets/AssetEventsHistory/DownloadAllEventsHistoryButton';
import { formatActionType } from '../helpers/formatAssetEventHistory';

const columnHelper = createColumnHelper<AssetEvent>();

// API only supports sorting by date (cursor-based pagination)
const SORT_BY_DATE = 'date';

type AssetEventsHistoryProps = {
  assetId: string;
  events: AssetEvent[];
  onLoadMore: () => void;
  isFetchingMore: boolean;
  hasMore: boolean;
  isLoadingTable: boolean;
};

export const AssetEventsHistory = ({
  assetId,
  events,
  onLoadMore,
  isFetchingMore,
  hasMore,
  isLoadingTable,
}: AssetEventsHistoryProps) => {
  const { t } = useTranslation('assets');
  const { space_m, space_l } = spaces;
  const { size_40 } = sizes;

  const [queryParams, setQueryParams] = useSearchParams();

  const sortOrder =
    (queryParams.get(AssetEventsHistoryQueryParams.SortOrder) as SortOrder) ||
    SortOrder.Desc;

  const handleSort = useCallback(
    (_orderBy: string, sortOrderValue: 'asc' | 'desc') => {
      // Only sorting by date is supported, orderBy is ignored
      const newSortOrder =
        sortOrderValue === 'asc' ? SortOrder.Asc : SortOrder.Desc;
      queryParams.set(AssetEventsHistoryQueryParams.SortOrder, newSortOrder);
      setQueryParams(queryParams, { replace: true });
    },
    [queryParams, setQueryParams]
  );

  const tableColumns = useMemo(
    () => [
      columnHelper.display({
        header: t('eventsHistory.columns.event'),
        id: 'event',
        meta: { minWidth: '400px', maxWidth: '550px', flex: '2' },
        cell: ({ row }) => {
          return (
            <TableCellFlexContainer>
              <EventCell event={row.original} />
            </TableCellFlexContainer>
          );
        },
      }),
      columnHelper.display({
        header: t('eventsHistory.columns.dateTime'),
        id: 'date-time',
        meta: {
          minWidth: '150px',
          flex: '0.5',
          enableBackendSorting: true,
          sortKey: SORT_BY_DATE,
          sortDescFirst: true,
        },
        cell: ({ row }) => {
          const date = formatDate(row.original.date, {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
          });
          const time = formatDate(row.original.date, {
            hour: '2-digit',
            minute: '2-digit',
          });
          return (
            <TableCellFlexContainer>
              <Text variant="body-2">{`${date} - ${time}`}</Text>
            </TableCellFlexContainer>
          );
        },
      }),
      columnHelper.display({
        header: t('eventsHistory.columns.performedBy'),
        id: 'performed-by',
        meta: { minWidth: '150px', flex: '0.3' },
        cell: ({ row }) => {
          return (
            <TableCellFlexContainer>
              <PerformedByCell
                performedBy={row.original.performedBy}
                eventSource={row.original.source}
              />
            </TableCellFlexContainer>
          );
        },
      }),
      columnHelper.display({
        header: t('eventsHistory.columns.eventType'),
        id: 'event-type',
        meta: { minWidth: '150px', flex: '0.5' },
        cell: ({ row }) => {
          const { actionType } = row.original;
          const eventHistoryActionType =
            EventTypeMap[actionType] ?? actionType.toLowerCase();

          const formattedActionType = formatActionType(eventHistoryActionType);
          return (
            <TableCellFlexContainer>
              <Text variant="body-2">
                {t(`eventsHistory.eventTypes.${formattedActionType}`)}
              </Text>
            </TableCellFlexContainer>
          );
        },
      }),
      columnHelper.display({
        header: t('eventsHistory.columns.category'),
        id: 'category',
        meta: { minWidth: '80px', flex: '0.3' },
        cell: ({ row }) => {
          const { category } = row.original;
          const eventHistoryCategory =
            CategoryMap[category] ?? row.original.category;

          return (
            <TableCellFlexContainer>
              <Text variant="body-2">
                {t(
                  `eventsHistory.categories.${eventHistoryCategory.toLowerCase()}`
                )}
              </Text>
            </TableCellFlexContainer>
          );
        },
      }),
    ],
    [t]
  );

  return (
    <TableContainer flexDirection="column" width="100%">
      <Flex
        width="100%"
        px={space_l}
        py={space_m}
        overflowX="hidden"
        vAlignContent="center"
        hAlignContent="between">
        <Flex vAlignContent="center" width="100%">
          <Text variant="heading-2">{t('eventsHistory.title')}</Text>
          <DownloadAllEventsHistoryButton assetId={assetId} />
        </Flex>
      </Flex>
      <Divider />
      <Flex width="100%" flexDirection="column">
        <TableFiltersGroup />
        <Divider />
        {/* TODO: Replace custom InfiniteScroll wrapper with built-in Table component infinite scroll functionality
             once it's implemented. This will eliminate the need for separate InfiniteScroll component wrapping.
             Ticket: https://electricai.atlassian.net/browse/HY-1835 */}
        <InfiniteScroll
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          rootMargin={size_40}
          targetId="asset-events-history-table">
          <Table
            id="asset-events-history-table"
            columns={tableColumns}
            data={events}
            loading={isLoadingTable}
            manualPagination
            manualSorting
            sortConfig={{
              orderBy: SORT_BY_DATE,
              sortOrder: sortOrder === SortOrder.Asc ? 'asc' : 'desc',
            }}
            onManualSort={handleSort}
            emptyStateText={t('common:userAccountsTable.noResults')}
          />
        </InfiniteScroll>
      </Flex>
    </TableContainer>
  );
};
